import { Loader } from '@unistocks/components/loading/spinner/loader';
import { QueryState } from '@unistocks/components/query-state/query-state';
import { SavingsGoalLayout } from '@unistocks/features/dashboard/components/dashboard-data/savings-goal/layout';
import {
  DashboardItemFrame,
  DashboardItemProps
} from '@unistocks/features/dashboard/components/dashboard-item/dashboard-item';
import { DashboardItemHeader } from '@unistocks/features/dashboard/components/dashboard-item/dashboard-item-header';
import { SavingsGoalProgressBar } from '@unistocks/features/savings-goals/components/savings-goal-progress-bar';
import {
  savingsGoalsGetQueryKey,
  savingsGoalsGetSavingsGoalProgressQueryKey,
  useSavingsGoalsGetQuery,
  useSavingsGoalsGetSavingsGoalProgressQuery
} from '@unistocks/proxy/axios-client/SavingsGoalsQuery';
import { t } from '@unistocks/shared/localization/index.ts';
import { useRouter } from 'expo-router';
import React from 'react';

const SavingsGoalChart = React.lazy(
  () => import('@unistocks/features/savings-goals/components/chart/savings-goal-chart')
);

export const SavingsGoal: React.FC<DashboardItemProps> = ({
  item,
  openSettings,
  ...frameProps
}) => {
  const { settings } = item;
  const savingsGoalId = settings.savingsGoal!;

  const router = useRouter();
  const { data: savingsGoal, isSuccess: isSuccessSavingsGoal } = useSavingsGoalsGetQuery({
    id: savingsGoalId
  });
  const { data: progress, isSuccess: isSuccessProgress } =
    useSavingsGoalsGetSavingsGoalProgressQuery({ id: savingsGoalId });

  const title = settings.title?.trim() || savingsGoal?.name;

  const navigateToSavingsGoal = () =>
    router.navigate(
      {
        pathname: '/(tabs)/(menu)/savings-goals/[id]',
        params: { id: savingsGoalId }
      },
      { withAnchor: true }
    );

  return (
    <DashboardItemFrame {...frameProps}>
      <DashboardItemHeader
        item={item}
        title={title}
        openSettings={openSettings}
        onPress={navigateToSavingsGoal}
      />
      <QueryState
        queryKeys={[
          savingsGoalsGetQueryKey(savingsGoalId),
          savingsGoalsGetSavingsGoalProgressQueryKey(savingsGoalId)
        ]}
        size={'$5'}
        flex={1}
        py={0}
        renderLoading={<SavingsGoalLoader />}
      >
        {isSuccessSavingsGoal && isSuccessProgress
          ? (() => {
              switch (settings.layout) {
                case SavingsGoalLayout.Chart:
                  return (
                    <React.Suspense fallback={<SavingsGoalLoader />}>
                      <SavingsGoalChart
                        savingsGoal={savingsGoal}
                        goalCalculation={progress}
                        containerProps={{ '$platform-web': { flex: 1 } }}
                        chartContainerProps={{ h: 350, '$platform-web': { flex: 1 } }}
                        minimizedDatePeriod
                      />
                    </React.Suspense>
                  );

                case SavingsGoalLayout.ProgressBar:
                  return (
                    <SavingsGoalProgressBar savingsGoal={savingsGoal} goalCalculation={progress} />
                  );
              }
            })()
          : null}
      </QueryState>
    </DashboardItemFrame>
  );
};

const SavingsGoalLoader = () => (
  <Loader size={'$5'} textProps={{ size: '$6' }}>
    {t('AbpUi::LoadingWithThreeDot')}
  </Loader>
);
